import React, { useEffect, useState, useMemo } from "react"
import "./PPPriceTrendStatistics.css"
import { gql, useQuery } from "@apollo/client"
import PPLoadingIndicator from "../layout/PPLoadingIndicator"
import PPErrorDisplay from "../layout/PPErrorDisplay"
import { useTranslation } from "react-i18next"
import { PPDefaultSelector } from "../ui/PPDefaultSelector"
import { MultiValueLabel, Option } from "../pricetable/PPShopPreset"
import { components } from "react-select"
import countries from "i18n-iso-countries"
import i18n_iso_countrie_de from "i18n-iso-countries/langs/de.json"
import i18n_iso_countrie_en from "i18n-iso-countries/langs/en.json"
import PPUtils from "../../utils/PPUtils"
import { PPDonutTrendBox, TrendOverview, TrendByCategoryTable, TrendByShopTable } from "./trendTables"

countries.registerLocale(i18n_iso_countrie_de)
countries.registerLocale(i18n_iso_countrie_en)

const EMPTY_SHOPS = []

const QUERY = gql`
    query getStats($department: String, $shopIds: [Long], $trendDays: Int) {
        stats(department: $department, shopIds: $shopIds) {
            priceTrendStatistics(days: $trendDays) {
                trendDistribution {
                    risingCount
                    fallingCount
                    unchangedCount
                    risingPercent
                    fallingPercent
                    unchangedPercent
                    totalProducts
                }
                averageTrend
                trendByCategory {
                    categoryName
                    risingCount
                    fallingCount
                    unchangedCount
                    totalCount
                    averageTrend
                }
                trendByShop {
                    shopId
                    shopName
                    lang
                    risingCount
                    fallingCount
                    unchangedCount
                    totalCount
                    averageTrend
                }
            }
        }
    }
`

function PPPriceTrendStatistics(props) {
    const [t, i18n] = useTranslation()
    const [selectedShopId, setSelectedShopId] = useState(null)
    const [selectedLang, setSelectedLang] = useState(null)
    const [customerActiveShops, setCustomerActiveShops] = useState([])
    const [trendDays, setTrendDays] = useState(7)
    const [activeTrendType, setActiveTrendType] = useState(null) // "rising", "falling", "unchanged"
    const [activeTab, setActiveTab] = useState("overview") // "overview", "category", "shop"

    useEffect(() => {
        const next =
            props.customerActiveShops && props.customerActiveShops.length > 0
                ? props.customerActiveShops
                : EMPTY_SHOPS
        setCustomerActiveShops((prev) => (prev === next || (prev.length === 0 && next.length === 0) ? prev : next))
    }, [props.customerActiveShops])

    const languages = useMemo(() => {
        if (!customerActiveShops || customerActiveShops.length === 0) return []
        
        const uniqueLangs = [...new Set(customerActiveShops.map((shop) => shop.lang).filter(Boolean))]
        return uniqueLangs.map((lang) => ({
            value: lang,
            label: lang.toUpperCase(),
            lang: lang
        }))
    }, [customerActiveShops])

    const shopsForSelectedLang = useMemo(() => {
        if (!customerActiveShops || customerActiveShops.length === 0) return []
        
        if (!selectedLang) return []
        
        const filteredShops = customerActiveShops.filter((shop) => shop.lang === selectedLang)

        const shopOptions = filteredShops.map((shop) => ({
            value: shop.id,
            label: shop.name,
            lang: shop.lang
        }))

        // Füge "Alle Shops" Option am Anfang hinzu
        return [
            {
                value: "all",
                label: t("dashboard.all_shops") || "Alle Shops",
                lang: selectedLang
            },
            ...shopOptions
        ]
    }, [customerActiveShops, selectedLang, t])

    const timeRangeOptions = [
        { value: 3, label: t("dashboard.days_3") || "3 Tage" },
        { value: 7, label: t("dashboard.days_7") || "7 Tage" }
    ]

    useEffect(() => {
        if (selectedLang === null && languages.length > 0) {
            const firstLang = languages[0]?.value
            setSelectedLang(firstLang)
        }
    }, [languages])

    useEffect(() => {
        if (selectedLang !== null) {
            const shopsForLang = customerActiveShops.filter((shop) => shop.lang === selectedLang)
            if (shopsForLang.length > 0 && selectedShopId === null) {
                // Setze default auf "Alle Shops"
                setSelectedShopId("all")
            } else if (shopsForLang.length === 0) {
                setSelectedShopId(null)
            } else {
                const currentShop = customerActiveShops.find((shop) => shop.id === selectedShopId)
                if (currentShop && currentShop.lang !== selectedLang) {
                    // Wenn der aktuelle Shop nicht zur neuen Sprache passt, setze auf "Alle Shops"
                    setSelectedShopId("all")
                }
            }
        }
    }, [selectedLang, customerActiveShops])

    const shopIdsForCurrentLang = useMemo(() => {
        if (!selectedLang || !customerActiveShops || customerActiveShops.length === 0) return EMPTY_SHOPS
        return customerActiveShops
            .filter((shop) => shop.lang === selectedLang)
            .map((shop) => shop.id)
    }, [customerActiveShops, selectedLang])

    // Variablen mit useMemo, damit sie neu berechnet werden, wenn sich shopIdsForCurrentLang ändert
    const variables = useMemo(() => {
        const vars = {
            department:
                props?.changeFilterDepartment != null && props?.changeFilterDepartment?.name != ""
                    ? props?.changeFilterDepartment?.name
                    : "",
            trendDays: trendDays
        }

        // Shop-IDs setzen: Wenn "all" ausgewählt ist, alle Shops der aktuellen Sprache verwenden
        // Wenn ein spezifischer Shop ausgewählt ist, nur diesen Shop verwenden
        if (selectedShopId !== null && selectedShopId !== "all") {
            vars.shopIds = [selectedShopId]
        } else if (selectedShopId === "all" && shopIdsForCurrentLang.length > 0) {
            vars.shopIds = shopIdsForCurrentLang
        }

        return vars
    }, [props.changeFilterDepartment, selectedShopId, trendDays, shopIdsForCurrentLang])

    const hasShops = customerActiveShops && customerActiveShops.length > 0
    const waitForShop = (props.customerActiveShops?.length > 0 || hasShops) && selectedShopId == null

    const { loading, error, data } = useQuery(QUERY, {
        displayName: "PPPriceTrendStatisticsQuery",
        variables: variables,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first",
        skip: waitForShop
    })

    if (waitForShop || (loading && !data)) return <PPLoadingIndicator />
    if (error)
        return (
            <div className="col-lg-12">
                <PPErrorDisplay error={error} />
            </div>
        )

    const trendStats = data?.stats?.priceTrendStatistics
    const selectedLangOption = languages.find((opt) => opt.value === selectedLang) || languages[0]
    const selectedShopOption = shopsForSelectedLang.find((opt) => opt.value === selectedShopId) || (shopsForSelectedLang.length > 0 ? shopsForSelectedLang[0] : null)

    const SingleValueWithFlag = ({ children, ...props }) => {
        const { data } = props
        return (
            <components.SingleValue {...props}>
                {data.lang && <span className={"fi fi-" + data.lang + " pp-price-trend-flag-icon"} />}
                {children}
            </components.SingleValue>
        )
    }

    const SingleValueShopWithFlag = ({ children, ...props }) => {
        const { data } = props
        return (
            <components.SingleValue {...props}>
                {data.lang && <span className={"fi fi-" + data.lang + " pp-price-trend-flag-icon"} />}
                {children}
            </components.SingleValue>
        )
    }

    return (
        <div className="col-lg-12 connectedSortable ui-sortable">
            <div className="card shadow d-flex flex-fill">
                <div className="card-header d-flex flex-wrap align-items-center gap-2">
                    <h3 className="card-title mb-0">
                        <i className="fas fa-chart-line me-2"></i>
                        {t("dashboard.price_trends") || "Preis Trends"}
                    </h3>
                    <div className="pp-card-header-tools">
                        <div className="pp-price-trend-selector-time-range">
                            <PPDefaultSelector
                                id="selectTrendDays"
                                data={timeRangeOptions}
                                placeholder={t("dashboard.time_range") || "Zeitraum"}
                                onChange={(option) => {
                                    setTrendDays(option?.value || 7)
                                }}
                                value={timeRangeOptions.find((opt) => opt.value === trendDays)}
                                isClearable={false}
                            />
                        </div>
                        {hasShops && (
                            <>
                                <div className="pp-price-trend-selector-language">
                                    <PPDefaultSelector
                                        id="selectPriceTrendLang"
                                        data={languages}
                                        components={{
                                            MultiValueLabel,
                                            Option,
                                            SingleValue: SingleValueWithFlag
                                        }}
                                        placeholder={t("single.language")}
                                        onChange={(option) => {
                                            setSelectedLang(option?.value || null)
                                        }}
                                        value={selectedLangOption}
                                        isClearable={false}
                                    />
                                </div>
                                <div className="pp-price-trend-selector-shop">
                                    <PPDefaultSelector
                                        id="selectPriceTrendShop"
                                        data={shopsForSelectedLang}
                                        components={{
                                            MultiValueLabel,
                                            Option,
                                            SingleValue: SingleValueShopWithFlag
                                        }}
                                        placeholder={t("single.shops")}
                                        onChange={(option) => {
                                            setSelectedShopId(option?.value || null)
                                        }}
                                        value={selectedShopOption}
                                        isDisabled={!selectedLang || shopsForSelectedLang.length === 0}
                                        isClearable={false}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="card-body">
                    {trendStats?.trendDistribution ? (
                        <>
                            <div className="row mb-2">
                                <div className="col-12">
                                    <div className="text-muted mb-0 py-2">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <strong className="pp-price-trend-average-label">{t("dashboard.average_trend") || "Durchschnittlicher Trend"}:</strong>{" "}
                                                <span className={`pp-price-trend-average-value ${trendStats.averageTrend > 0 ? "text-success" : trendStats.averageTrend < 0 ? "text-danger" : "text-secondary"}`}>
                                                    {trendStats.averageTrend > 0 ? "+" : ""}
                                                    {(trendStats.averageTrend || 0).toFixed(2)}%
                                                </span>
                                            </div>
                                            <div>
                                                {trendStats.trendDistribution.totalProducts || 0} {t("dashboard.products") || "Produkte"}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <PPDonutTrendBox
                                    value={trendStats.trendDistribution.risingPercent || 0}
                                    count={trendStats.trendDistribution.risingCount || 0}
                                    text={t("dashboard.trend_rising") || "Preise steigend"}
                                    explanation={t("dashboard.trend_explanation_rising") || "Produkte mit steigendem Preis im gewählten Zeitraum"}
                                    startColor={PPUtils.Colors.GreenStart}
                                    endColor={PPUtils.Colors.GreenEnd}
                                    onOpenClick={() => {
                                        if (activeTrendType === "rising") {
                                            setActiveTrendType(null)
                                        } else {
                                            setActiveTrendType("rising")
                                        }
                                    }}
                                    showMore={activeTrendType === "rising"}
                                    trendType="rising"
                                    selectedShopId={selectedShopId}
                                    shopIdsForCurrentLang={shopIdsForCurrentLang}
                                />
                                <PPDonutTrendBox
                                    value={trendStats.trendDistribution.fallingPercent || 0}
                                    count={trendStats.trendDistribution.fallingCount || 0}
                                    text={t("dashboard.trend_falling") || "Preise fallend"}
                                    explanation={t("dashboard.trend_explanation_falling") || "Produkte mit fallendem Preis im gewählten Zeitraum"}
                                    startColor={PPUtils.Colors.RedStart}
                                    endColor={PPUtils.Colors.RedEnd}
                                    onOpenClick={() => {
                                        if (activeTrendType === "falling") {
                                            setActiveTrendType(null)
                                        } else {
                                            setActiveTrendType("falling")
                                        }
                                    }}
                                    showMore={activeTrendType === "falling"}
                                    trendType="falling"
                                    selectedShopId={selectedShopId}
                                    shopIdsForCurrentLang={shopIdsForCurrentLang}
                                />
                                <PPDonutTrendBox
                                    value={trendStats.trendDistribution.unchangedPercent || 0}
                                    count={trendStats.trendDistribution.unchangedCount || 0}
                                    text={t("dashboard.trend_unchanged") || "Preise unverändert"}
                                    explanation={t("dashboard.trend_explanation_unchanged") || "Produkte mit unverändertem Preis im gewählten Zeitraum"}
                                    startColor={PPUtils.Colors.NeutralStart}
                                    endColor={PPUtils.Colors.NeutralEnd}
                                    onOpenClick={() => {
                                        if (activeTrendType === "unchanged") {
                                            setActiveTrendType(null)
                                        } else {
                                            setActiveTrendType("unchanged")
                                        }
                                    }}
                                    showMore={activeTrendType === "unchanged"}
                                    trendType="unchanged"
                                    selectedShopId={selectedShopId}
                                    shopIdsForCurrentLang={shopIdsForCurrentLang}
                                />
                            </div>

                            <div className="card shadow card-primary card-outline card-outline-tabs mt-3">
                                    <div className="card-header p-0 border-bottom-0">
                                        <ul className="nav nav-tabs" id="custom-tabs-trend-tab" role="tablist">
                                            <li className="nav-item">
                                                <a
                                                    className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                                                    id="custom-tabs-trend-overview-tab"
                                                    data-bs-toggle="pill"
                                                    href="#custom-tabs-trend-overview"
                                                    role="tab"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setActiveTab("overview")
                                                        window.dispatchEvent(new Event("resize"))
                                                    }}
                                                    aria-controls="custom-tabs-trend-overview"
                                                    aria-selected={activeTab === "overview"}>
                                                    {t("dashboard.overview") || "Übersicht"}
                                                </a>
                                            </li>
                                            <li className="nav-item">
                                                <a
                                                    className={`nav-link ${activeTab === "category" ? "active" : ""}`}
                                                    id="custom-tabs-trend-category-tab"
                                                    data-bs-toggle="pill"
                                                    href="#custom-tabs-trend-category"
                                                    role="tab"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setActiveTab("category")
                                                        window.dispatchEvent(new Event("resize"))
                                                    }}
                                                    aria-controls="custom-tabs-trend-category"
                                                    aria-selected={activeTab === "category"}>
                                                    {t("single.categories")}
                                                </a>
                                            </li>
                                            <li className="nav-item">
                                                <a
                                                    className={`nav-link ${activeTab === "shop" ? "active" : ""}`}
                                                    id="custom-tabs-trend-shop-tab"
                                                    data-bs-toggle="pill"
                                                    href="#custom-tabs-trend-shop"
                                                    role="tab"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        setActiveTab("shop")
                                                        window.dispatchEvent(new Event("resize"))
                                                    }}
                                                    aria-controls="custom-tabs-trend-shop"
                                                    aria-selected={activeTab === "shop"}>
                                                    {t("single.shops")}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div className="card-body">
                                        <div className="tab-content" id="custom-tabs-trend-tabContent">
                                            <div
                                                className={`tab-pane fade ${activeTab === "overview" ? "active show" : ""}`}
                                                id="custom-tabs-trend-overview"
                                                role="tabpanel"
                                                aria-labelledby="custom-tabs-trend-overview-tab">
                                                <TrendOverview
                                                    trendDistribution={trendStats.trendDistribution}
                                                />
                                            </div>
                                            <div
                                                className={`tab-pane fade ${activeTab === "category" ? "active show" : ""}`}
                                                id="custom-tabs-trend-category"
                                                role="tabpanel"
                                                aria-labelledby="custom-tabs-trend-category-tab">
                                                <TrendByCategoryTable 
                                                    items={trendStats.trendByCategory || []} 
                                                    activeTrendType={activeTrendType}
                                                    selectedShopId={selectedShopId}
                                                    selectedShopName={selectedShopId !== null && selectedShopId !== "all" ? (() => {
                                                        const foundShop = customerActiveShops.find((shop) => shop.id === selectedShopId)
                                                        // Verwende shortName falls verfügbar, sonst name, sonst null
                                                        if (foundShop) {
                                                            return foundShop.shortName || foundShop.name || null
                                                        }
                                                        return null
                                                    })() : null}
                                                    selectedLang={selectedShopId !== null && selectedShopId !== "all" ? (() => {
                                                        const foundShop = customerActiveShops.find((shop) => shop.id === selectedShopId)
                                                        return foundShop?.lang || selectedLang
                                                    })() : selectedLang}
                                                    shopIdsForCurrentLang={shopIdsForCurrentLang}
                                                />
                                            </div>
                                            <div
                                                className={`tab-pane fade ${activeTab === "shop" ? "active show" : ""}`}
                                                id="custom-tabs-trend-shop"
                                                role="tabpanel"
                                                aria-labelledby="custom-tabs-trend-shop-tab">
                                                <TrendByShopTable 
                                                    items={trendStats.trendByShop || []} 
                                                    i18n={i18n}
                                                    activeTrendType={activeTrendType}
                                                    selectedShopId={selectedShopId}
                                                    selectedShopName={selectedShopId !== null && selectedShopId !== "all" ? (() => {
                                                        const foundShop = customerActiveShops.find((shop) => shop.id === selectedShopId)
                                                        // Verwende shortName falls verfügbar, sonst name, sonst null
                                                        if (foundShop) {
                                                            return foundShop.shortName || foundShop.name || null
                                                        }
                                                        return null
                                                    })() : null}
                                                    selectedLang={selectedShopId !== null && selectedShopId !== "all" ? (() => {
                                                        const foundShop = customerActiveShops.find((shop) => shop.id === selectedShopId)
                                                        return foundShop?.lang || selectedLang
                                                    })() : selectedLang}
                                                    shopIdsForCurrentLang={shopIdsForCurrentLang}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                        </>
                    ) : (
                        <p className="text-center text-muted">{t("single.no_results") || "Keine Daten verfügbar"}</p>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PPPriceTrendStatistics
